// Hook semantycznego wyszukiwania ofert handlowca - GET /sales-crm/trader/offers/semantic-search.
// RAG: Qdrant + OpenAI embeddings. Filtruje TYLKO oferty zalogowanego handlowca.

import { axios } from 'api';
import { useQuery, UseQueryOptions } from 'react-query';
import { ICommandResponseError as IError } from 'api/types';

export interface ISemanticSearchTopProduct {
  id: number | null;
  name: string | null;
  symbol: string | null;
}

export interface ISemanticSearchPriceThreshold {
  qtyFrom: number | null;
  price: number | null;
  productName: string | null;
}

export interface ISemanticSearchServiceEntry {
  name: string | null;
  priceType: 'UNIT' | 'TOTAL' | null;
  priceNet: number | null;
}

export interface ISemanticSearchOfferItem {
  offer_id: number;
  offer_number: string | null;
  client_id: number | null;
  client_name: string | null;
  client_city: string | null;
  trader_name: string | null;
  status: number | null; // 1..6 (Offer::STATUS_*)
  status_formatted: string | null;
  value_net: number | null;
  value_net_formatted: string | null;
  value_gross: number | null;
  value_gross_formatted: string | null;
  created_at: string | null;
  top_products: ISemanticSearchTopProduct[];
  has_price_thresholds: boolean;
  price_thresholds: ISemanticSearchPriceThreshold[];
  has_services: boolean;
  services_list: ISemanticSearchServiceEntry[];
  score: number;
}

export interface ISemanticSearchResponse {
  items: ISemanticSearchOfferItem[];
  total_count: number;
  execution_ms: number;
}

export interface IUseTraderSemanticSearchParams {
  q: string;
  limit?: number;
  offset?: number;
}

export const getSalesCrmTraderOffersSemanticSearchQueryKey = (
  params: IUseTraderSemanticSearchParams
) => ['sales-crm', 'trader-offers', 'semantic-search', params] as const;

const getSalesCrmTraderOffersSemanticSearch = (
  params: IUseTraderSemanticSearchParams
): Promise<ISemanticSearchResponse> => {
  const queryParams: Record<string, number | string> = { q: params.q };
  if (params.limit) queryParams.limit = params.limit;
  if (params.offset) queryParams.offset = params.offset;
  return axios.get('/sales-crm/trader/offers/semantic-search', { params: queryParams });
};

/**
 * Hook semantycznego wyszukiwania ofert handlowca.
 *
 * - `staleTime: 5000` (5s) — handlowiec moze edytowac oferte i od razu szukac.
 * - `enabled: q.length >= 3` — krotsze zapytania nie sa wysylane.
 * - Wynik error.status === 503 oznacza Qdrant down (frontend: toast).
 */
export const useGetSalesCrmTraderOffersSemanticSearch = (
  params: IUseTraderSemanticSearchParams,
  options?: UseQueryOptions<ISemanticSearchResponse, IError>
) =>
  useQuery<ISemanticSearchResponse, IError>(
    getSalesCrmTraderOffersSemanticSearchQueryKey(params),
    () => getSalesCrmTraderOffersSemanticSearch(params),
    {
      enabled: (params.q?.length ?? 0) >= 3,
      keepPreviousData: true,
      staleTime: 5_000,
      ...options
    }
  );
